import { ovl, SYSTableDefIds } from "../../../.."
import { postRequest } from "../../../../effects"
import { FeatureGet, FeatureInfo } from "../../../../global/globalapi"
import { isMobile, T } from "../../../../global/globals"
import { HookTableFn, HookTableFn_Type } from "../../../../global/hooks"
import {
  FieldSetVal,
  FieldVal,
  SYSField,
  SYSFormState,
} from "../../../forms/fieldapi"
import { sortedReplacer } from "../../../forms/helper"
import { SnackAdd } from "../../../snackapi"
import {
  _GetActiveInactiveForcedFilterCustom,
  _GetForcedColumnFiltersKeys,
  _ResetFilters,
  _ResetSort,
} from "../../helpers"
import {
  SYSTableDef,
  TableAttributeFilter,
  TableColumnFilter,
  TableCustomFilter,
  TableEditSaveRow,
  TableGet,
  TableRebuild,
} from "../../tableapi"

export const _GetFilterSettingsCardCodes = async () => {
  let state = ovl.getState()
  let defId = state.ovl.libState.tableSettings.filterSort.currentTableDefId
  let res = await postRequest(
    `${state.ovl.apiUrl}tablesettings/lookups/cardcode`,
    { tableDefId: defId }
  )
  state.ovl.libState.tableSettings.filterSort.lookups.CardCode = res.data.data
}

export const _GetFilterSettingsCntctCodes = async () => {
  let state = ovl.getState()
  let defId = state.ovl.libState.tableSettings.filterSort.currentTableDefId
  let res = await postRequest(
    `${state.ovl.apiUrl}tablesettings/lookups/u_cntctcode`,
    { tableDefId: defId }
  )
  state.ovl.libState.tableSettings.filterSort.lookups.U_CntctCode =
    res.data.data
}

export const _FilterChangedComparedToDefault = (def: SYSTableDef) => {
  if (!def.initialised) {
    return false
  }
  let current = JSON.stringify(_GetCurrentFilters(def))
  let _default = JSON.stringify(_GetDefaultFilters(def))
  return current !== _default
}

export const _SortChangedComparedToDefault = (def: SYSTableDef) => {
  return (
    JSON.stringify(_GetCurrentSorts(def)) !==
    JSON.stringify(_GetDefaultSorts(def))
  )
}

export const _SetCurrentActiveTableSettingsFilter = async (
  key: string,
  tableDefId: SYSTableDefIds
): Promise<boolean> => {
  try {
    let tableDef = TableGet(tableDefId)
    let val
    let desc
    let def = tableDef.def
    let uiState = def.uiState
    if (key) {
      let settingsTable = TableGet("SYSTableSettings_FilterSort", {
        byIdValue: key,
      })
      let sTable = settingsTable.rows[settingsTable.getKeys()[0]]
      val = sTable["U_FilterSortSettings"]
      desc = sTable["U_Desc"]
    }
    if (val) {
      let persistableFilterSortSettings = JSON.parse(val)

      // we need before reset to validate against forcedFilters
      // currently we just have custom Filters supported for forcedFilters
      if (
        def.uiState.filter._forced?.custom &&
        persistableFilterSortSettings.customFilters
      ) {
        const { activeKeySet, inactiveKeySet } =
          _GetActiveInactiveForcedFilterCustom(def)

        if (
          Object.keys(persistableFilterSortSettings.customFilters).some(
            (k) =>
              inactiveKeySet.has(k) &&
              !def.uiState.filter._forced.custom.groups[
                def.uiState.filter._initial_filterCustomFlat[k]._groupKey
              ].visible
          )
        ) {
          SnackAdd(
            T("SYSTableSettingsFavoriteNotValidInThisContext"),
            "Warning"
          )
          return false
        }
      }

      if (def.uiState.filter._forced?.forcedColumnFilters) {
        const forcedColumnFiltersKeysSet = _GetForcedColumnFiltersKeys(def)

        let hasConflict = false
        if (persistableFilterSortSettings.columnFilters) {
          hasConflict = Object.keys(
            persistableFilterSortSettings.columnFilters
          ).some((k) => forcedColumnFiltersKeysSet.has(k))
        }
        if (!hasConflict && persistableFilterSortSettings.rangeFilters) {
          hasConflict = Object.keys(
            persistableFilterSortSettings.rangeFilters
          ).some((k) => forcedColumnFiltersKeysSet.has(k))
        }

        if (hasConflict) {
          SnackAdd(
            T("SYSTableSettingsFavoriteNotValidInThisContext"),
            "Warning"
          )
          return false
        }
      }

      await _ResetFilters(def, { key, settings: val })
      _ResetSort(def)

      if (persistableFilterSortSettings.columnFilters) {
        Object.keys(persistableFilterSortSettings.columnFilters).forEach(
          async (k) => {
            if (
              !(await TableColumnFilter(
                def.id,
                k,
                persistableFilterSortSettings.columnFilters[k].list,
                {
                  exclusive:
                    persistableFilterSortSettings.columnFilters[k].exclusive,
                  _innerEvent: true,
                }
              ))
            ) {
              return
            }
          }
        )
      }
      if (persistableFilterSortSettings.rangeFilters) {
        Object.keys(persistableFilterSortSettings.rangeFilters).forEach((k) => {
          if (uiState.filter.columns[k]) {
            uiState.filter.columns[k].rangeFilter =
              persistableFilterSortSettings.rangeFilters[k]
          }
        })
      }

      if (persistableFilterSortSettings.customFilters) {
        Object.keys(persistableFilterSortSettings.customFilters).forEach(
          async (k) => {
            if (
              !(await TableCustomFilter(
                def.id,
                k,
                persistableFilterSortSettings.customFilters[k],
                { _innerEvent: true }
              ))
            ) {
              return
            }
          }
        )
      }

      if (persistableFilterSortSettings.attributeFilters) {
        let filterAttributes = persistableFilterSortSettings.attributeFilters
        let filterAttributesInitial = def.options.initial_rowAttributes
        await Promise.all(
          Object.keys(filterAttributesInitial).map(async (defKey) =>
            Object.keys(filterAttributesInitial[defKey].attributes)
              .concat(
                Object.keys(
                  filterAttributes[defKey]?.attributes
                    ? filterAttributes[defKey].attributes
                    : {}
                )
              )
              .map(async (attributeKey) => {
                let val = !!filterAttributes[defKey].attributes?.[attributeKey]
                if (
                  !(await TableAttributeFilter(
                    def.id,
                    defKey,
                    attributeKey,
                    val,
                    {
                      _innerEvent: true,
                    }
                  ))
                ) {
                  return
                }
              })
          )
        )
      }

      if (persistableFilterSortSettings.tableFilter) {
        if (persistableFilterSortSettings.tableFilter.value) {
          uiState.filter.filter.value =
            persistableFilterSortSettings.tableFilter.value
        }
        // ignore showSelected for now. Its not that easy. We need to save the ids of all selected and then transform them back to selected as needed
        if (
          persistableFilterSortSettings.tableFilter.showSelected !== undefined
        ) {
          uiState.filter.filter.showSelected =
            persistableFilterSortSettings.tableFilter.showSelected
        }
      }

      if (persistableFilterSortSettings.tableSort) {
        if (def.columns[persistableFilterSortSettings.tableSort[0].field]) {
          uiState.sort.sort = persistableFilterSortSettings.tableSort[0]
        }
      }

      if (persistableFilterSortSettings.customSort === undefined) {
        persistableFilterSortSettings.customSort = ""
      }
      if (
        !persistableFilterSortSettings.customSort ||
        def.uiState.sort._initial_sortCustomFlat?.[
          persistableFilterSortSettings.customSort
        ]
      ) {
        if (uiState.sort.sortCustom) {
          uiState.sort.sortCustom.selected =
            persistableFilterSortSettings.customSort
        }
      }
    } else {
      await _ResetFilters(def, { key, settings: val })
      _ResetSort(def)
    }
    uiState.currentSelectedFilterSortDesc = desc
    uiState.currentSelectedFilterSortValue = val
    uiState.currentSelectedFilterSort = key
    return true
  } catch (e) {
    console.error(e)
  }
}

export const _GetCurrentActiveTableSettingsFilterSortJsonString = (
  tableDefId: SYSTableDefIds,
  ignoreSort: boolean = false
) => {
  let persistableFilterSortSettings = _GetCurrentActiveTableSettingsFilterSort(
    tableDefId,
    ignoreSort
  )
  return JSON.stringify(persistableFilterSortSettings, sortedReplacer)
}

export const _GetCurrentActiveTableSettingsFilterSort = (
  tableDefId: SYSTableDefIds,
  ignoreSort: boolean = false
) => {
  let def = TableGet(tableDefId).def
  let {
    columnFilters,
    rangeFilters,
    customFilters,
    attributeFilters,
    tableFilter,
  } = _GetCurrentFilters(def)

  // optimize save format
  if (Object.keys(customFilters).length === 0) {
    customFilters = undefined
  }
  if (Object.keys(attributeFilters).length === 0) {
    attributeFilters = undefined
  }

  if (Object.keys(columnFilters).length === 0) {
    columnFilters = undefined
  }
  if (Object.keys(rangeFilters).length === 0) {
    rangeFilters = undefined
  }

  let { customSort, tableSort } = _GetCurrentSorts(def)
  if (ignoreSort) {
    customSort = undefined
    tableSort = undefined
  }
  let persistableFilterSortSettings = {
    version: 2,
    ts: new Date().toISOString(),
    columnFilters,
    rangeFilters,
    customFilters,
    attributeFilters,
    tableFilter,
    customSort,
    tableSort,
  }
  return persistableFilterSortSettings
}

const _GetCurrentSorts = (def: SYSTableDef) => {
  let uiState = def.uiState
  let customSort = uiState.sort.sortCustom?.selected

  if (customSort === "") {
    customSort = undefined
  }
  let tableSort = undefined
  if (!customSort) {
    tableSort = [uiState.sort.sort]
  }
  return { customSort, tableSort }
}

const _GetDefaultSorts = (def: SYSTableDef) => {
  let options = def.options
  let customSort = ""
  let sortCustomFlat = def.uiState.sort._initial_sortCustomFlat
  if (sortCustomFlat) {
    Object.keys(sortCustomFlat).some((s) => {
      if (sortCustomFlat[s].selected) {
        customSort = s
        return true
      }
    })
  }
  if (customSort === "") {
    customSort = undefined
  }
  let tableSort = undefined
  if (!customSort) {
    tableSort = [
      {
        direction: options.initial_sort.direction,
        field: options.initial_sort.field,
        useValue: options.initial_sort.useValue,
      },
    ]
  }
  return { customSort, tableSort }
}

const _GetCurrentFilters = (def: SYSTableDef) => {
  let uiState = def.uiState
  let columns = uiState.filter.columns
  let allCustomFilters = uiState.filter.filterCustom
  let allCustomFiltersFlat = def.uiState.filter._initial_filterCustomFlat
  let columnFilters = Object.keys(columns)
    .filter(
      (f) =>
        def.columns[f] &&
        columns[f].columnFilter &&
        columns[f].columnFilter.type !== "none"
    )
    .reduce((val, m) => {
      val[m] = {
        type: columns[m].columnFilter.type,
        list: columns[m].columnFilter.list,
        exclusive: columns[m].columnFilter.exclusive,
      }
      return val
    }, {})

  let rangeFilters = Object.keys(columns)
    .filter(
      (f) =>
        columns[f].rangeFilter?.selected !== undefined &&
        columns[f].rangeFilter?.selected !== "none"
    )
    .reduce((val, m) => {
      val[m] = columns[m].rangeFilter
      return val
    }, {})

  let customFilters = Object.keys(allCustomFilters)
    .filter((f) => allCustomFiltersFlat[f] && allCustomFilters[f].active)
    .reduce((val, m) => {
      val[m] = allCustomFilters[m].active
      return val
    }, {})

  let tableFilter = {}
  if (uiState.filter.filter.value) {
    tableFilter["value"] = uiState.filter.filter.value
    //tableFilter["showSelected"] = uiState.filter.filter.showSelected
  }
  // ignore for now see explanation in _Set...
  if (uiState.filter.filter.showSelected) {
    tableFilter["showSelected"] = uiState.filter.filter.showSelected
  }
  let filterAttributes = uiState.filter.filterAttributes
  let attributeFilters = Object.keys(filterAttributes)
    .filter(
      (defKey) => Object.keys(filterAttributes[defKey].attributes).length > 0
    )
    .reduce((val, defKey) => {
      val[defKey] = { attributes: filterAttributes[defKey].attributes }
      return val
    }, {})
  return {
    columnFilters,
    rangeFilters,
    customFilters,
    attributeFilters,
    tableFilter,
  }
}

const _GetDefaultFilters = (def: SYSTableDef) => {
  let columns = def.columns
  let columnFilters = Object.keys(columns)
    .filter((f) => {
      return (
        columns[f].filter.columnFilter &&
        columns[f].filter.columnFilter.type !== "none"
      )
    })
    .reduce((val, m) => {
      val[m] = {
        type: columns[m].filter.columnFilter.type,
        list: columns[m].filter.columnFilter.list,
        exclusive: columns[m].filter.columnFilter.exclusive,
      }
      return val
    }, {})

  let rangeFilters = Object.keys(columns)
    .filter(
      (f) =>
        columns[f].filter.rangeFilter?.selected !== undefined &&
        columns[f].filter.rangeFilter?.selected !== "none"
    )
    .reduce((val, m) => {
      val[m] = columns[m].filter.rangeFilter
      return val
    }, {})

  let customFilters = {}
  let filterCustomFlat = def.uiState.filter._initial_filterCustomFlat
  if (filterCustomFlat) {
    Object.keys(filterCustomFlat).forEach((f) => {
      if (filterCustomFlat[f].active) {
        customFilters[f] = filterCustomFlat[f].active
      }
    })
  }

  let filterAttributes = def.options.initial_rowAttributes
  let attributeFilters = Object.keys(filterAttributes)
    .filter(
      (defKey) => Object.keys(filterAttributes[defKey].attributes).length > 0
    )
    .reduce((val, defKey) => {
      val[defKey] = { attributes: filterAttributes[defKey].attributes }
      return val
    }, {})

  let tableFilter = {}
  if (def.options.initial_filter.value) {
    tableFilter["value"] = def.options.initial_filter.value
  }
  if (def.options.initial_filter.showSelected) {
    tableFilter["showSelected"] = def.options.initial_filter.showSelected
  }

  return {
    columnFilters,
    rangeFilters,
    customFilters,
    attributeFilters,
    tableFilter,
  }
}

export const _FilterSortChanged = (
  tableDefId: SYSTableDefIds,
  ignoreSort: boolean = false
): { changed: boolean; favoriteInvalid: boolean } => {
  let tableDef = TableGet(tableDefId)
  let val
  if (tableDef.def.uiState.currentSelectedFilterSort) {
    val = tableDef.def.uiState.currentSelectedFilterSortValue
  } else {
    return { changed: false, favoriteInvalid: false }
  }
  let selected
  let favoriteDefsNotFoundInCurrent = false

  if (val) {
    let valParsed = JSON.parse(val)

    //if the filterCustom doesnt exist/is relevant anymore
    // eg after providing a new version wher this customfilter doesnt exist anymore

    //todo check whats in here if no czstomfiltzer is prevent (is it even there?)

    if (valParsed?.customFilters) {
      Object.keys(valParsed.customFilters).forEach((k) => {
        if (!tableDef.def.uiState.filter._initial_filterCustomFlat?.[k]) {
          delete valParsed.customFilters[k]
          favoriteDefsNotFoundInCurrent = true
        }
      })
    }

    //if the rangeFilter doesnt exist/is relevant anymore
    // eg. after providing a new tabledef (by a new version or by eg. ccondiguring columns like in actactivities) the column may not exist anymore
    if (valParsed?.rangeFilters) {
      Object.keys(valParsed.rangeFilters).forEach((k) => {
        if (!tableDef.def.columns[k]) {
          //delete valParsed.rangeFilters[k]
          favoriteDefsNotFoundInCurrent = true
        }
      })
      // if (
      //   valParsed.rangeFilters &&
      //   Object.keys(valParsed.rangeFilters).length === 0
      // ) {
      //   valParsed.rangeFilters = undefined
      // }
    }

    //if the columnFilter doesnt exist/is relevant anymore
    if (valParsed.columnFilters) {
      Object.keys(valParsed.columnFilters).forEach((k) => {
        if (!tableDef.def.columns[k]) {
          //delete valParsed.columnFilters[k]
          favoriteDefsNotFoundInCurrent = true
        }
      })
      //   if (
      //     valParsed.columnFilters &&
      //     Object.keys(valParsed.columnFilters).length === 0
      //   ) {
      //     valParsed.columnFilters = undefined
      //   }
      // }
    }
    //if the sortColumn doesnt exist/is relevant anymore
    if (valParsed.tableSort) {
      valParsed.tableSort.some((t) => {
        if (!tableDef.def.columns[t.field]) {
          favoriteDefsNotFoundInCurrent = true
          return true
        }
      })
    }

    //if the customSort doesnt exist/is relevant anymore
    if (valParsed.customSort) {
      if (
        !tableDef.def.uiState.sort._initial_sortCustomFlat?.[
          valParsed.customSort
        ]
      ) {
        favoriteDefsNotFoundInCurrent = true
      }
    }

    // if (favoriteDefsNotFoundInCurrent) {
    //   tableDef.def.uiState.currentSelectedFilterSortValue = JSON.stringify(
    //     valParsed,
    //     sortedReplacer
    //   )
    //   selected = tableDef.def.uiState.currentSelectedFilterSortValue
    // }

    // make sure to delete timestamp...
    delete valParsed.ts
    if (ignoreSort) {
      valParsed.customSort = undefined
      valParsed.tableSort = undefined
    }
    selected = JSON.stringify(valParsed, sortedReplacer)
  }
  let currentParsed = _GetCurrentActiveTableSettingsFilterSort(
    tableDefId,
    ignoreSort
  )

  // as well delete timestamp here
  delete currentParsed.ts
  let current = JSON.stringify(currentParsed, sortedReplacer)
  //console.log(current !== selected, current, selected)

  return {
    changed: current !== selected,
    favoriteInvalid: favoriteDefsNotFoundInCurrent,
  }
}
export type _FilterSortPermission = {
  readLevel: "own" | "company" | "all"
  modifyLevel?: "own" | "company" | "all"
  found: boolean
}

export const _GetFilterSortPermission = (
  tableDefId: string
): _FilterSortPermission => {
  let feature = FeatureGet(<any>"SYSTableSettings")
  if (!feature.Found()) {
    return {
      readLevel: "own",
      modifyLevel: undefined,
      found: false,
    }
  }

  let config = feature.GetJson1Parsed() || {}
  let matchConfig: { readLevel?: string; modifyLevel?: string } | undefined
  let foundMatch = false

  if (config.startsWith && tableDefId) {
    let longestMatchKey = ""
    for (const key of Object.keys(config.startsWith)) {
      if (tableDefId.startsWith(key) && key.length > longestMatchKey.length) {
        longestMatchKey = key
      }
    }
    if (longestMatchKey && config.startsWith[longestMatchKey].Favorites) {
      matchConfig = config.startsWith[longestMatchKey].Favorites
      foundMatch = true
    }
  }

  return {
    readLevel: <any>matchConfig?.readLevel || "own",
    modifyLevel: <any>matchConfig?.modifyLevel,
    found: foundMatch,
  }
}

export const _SetRangeFilter = async (
  formState: SYSFormState,
  def: SYSTableDef,
  clearFilter?: boolean
) => {
  let valueField = formState.fields.HeaderMenuRangeFilterValue
  let value2Field = formState.fields.HeaderMenuRangeFilterValue2
  let filterTypeField = formState.fields.HeaderMenuRangeFilterType

  let key = FieldVal(filterTypeField)
  if (!clearFilter) {
    if (key === "none") {
      if (
        def.columns[def.uiState.headerSelected].filter.rangeFilter
          .defaultFilterType
      ) {
        key =
          def.columns[def.uiState.headerSelected].filter.rangeFilter
            .defaultFilterType
      }
    }
  } else {
    key = "none"

    await FieldSetVal(valueField, null)
    await FieldSetVal(value2Field, null)
  }
  await FieldSetVal(filterTypeField, key)

  let val = FieldVal(valueField)
  let val2
  if (key === "greaterThanOrEqual") {
    val2 = FieldVal(value2Field)
  }
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        TableDefId: def.id,
        FnType: "ColumnRangeFilter",
        before: true,
        id: def.uiState.headerSelected,
        val1: key,
        val2: `${val},${val2 !== undefined ? val2 : ""}`,
      })
    ) {
      return false
    }
  }

  ovl.getActions().ovl.internal._TableSelectColumnRangeFilter({
    key,
    value: val,
    value2: val2,
    def,
  })

  await TableRebuild(def.id, { showProcess: true })

  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      TableDefId: def.id,
      FnType: "ColumnRangeFilter",
      before: false,
      id: def.uiState.headerSelected,
      val1: key,
      val2: `${val},${val2 !== undefined ? val2 : ""}`,
    })
  }
}
